import classNames from 'classnames';
import type { PropsWithChildren } from 'react';
import React, { startTransition, useCallback, useEffect, useRef, useState } from 'react';

import * as styles from './animated-expand-wrapper.module.scss';

type AnimatedExpandWrapperProps = PropsWithChildren<{
  isExpanded: boolean;
  desktopAlwaysOpen?: boolean;
  className?: string;
  childContainerClassName?: string;
  expandDurationSeconds?: number;
}>;

const AnimatedExpandWrapper = ({
  isExpanded,
  desktopAlwaysOpen,
  children,
  className,
  childContainerClassName,
  expandDurationSeconds = 0.3,
}: AnimatedExpandWrapperProps) => {
  const childContainerRef = useRef<HTMLDivElement | null>(null);
  const [childHeight, setChildHeight] = useState(0);

  const updateComponentHeight = useCallback(
    () =>
      startTransition(() => {
        const height = childContainerRef.current?.getBoundingClientRect().height;

        if (height !== undefined && height !== 0) {
          setChildHeight(height);
        }
      }),
    []
  );

  useEffect(() => {
    const childContainerElement = childContainerRef.current;
    if (!childContainerElement) return;

    updateComponentHeight();

    const resizeObserver = new ResizeObserver(updateComponentHeight);
    resizeObserver.observe(childContainerElement);
    return () => resizeObserver.disconnect();
  }, [updateComponentHeight]);

  useEffect(() => {
    if (isExpanded) {
      updateComponentHeight();
    }
  }, [isExpanded, updateComponentHeight]);

  return (
    <div
      data-testid={`container-${childHeight}`}
      className={classNames(
        isExpanded ? styles.visible : styles.hidden,
        desktopAlwaysOpen === true ? styles.desktopAlwaysOpen : null,
        className
      )}
      style={
        {
          '--expand-duration': `${expandDurationSeconds}s`,
          ...(isExpanded ? { '--child-height': `${childHeight}px` } : {}),
        } as React.CSSProperties
      }
    >
      <div ref={childContainerRef} className={childContainerClassName}>
        {children}
      </div>
    </div>
  );
};

export default AnimatedExpandWrapper;
